import { createContext, useContext, useState, useCallback } from 'react';
import { setClientApiKey } from '../api/client';

export interface Member {
  personId: number;
  firstName: string;
  lastName: string;
  email: string;
  siteId?: number;
  logoUrl?: string;
  appName?: string;
  qrCodeUrl?: string;
  isActive?: boolean;
}

interface MemberContextValue {
  member: Member | null;
  setMember: (m: Member, apiKey: string) => void;
  clearMember: () => void;
}

const MemberContext = createContext<MemberContextValue | null>(null);

function loadFromStorage(): Member | null {
  try {
    const raw = sessionStorage.getItem('member');
    return raw ? (JSON.parse(raw) as Member) : null;
  } catch {
    return null;
  }
}

export function MemberProvider({ children }: { children: React.ReactNode }) {
  const [member, setMemberState] = useState<Member | null>(loadFromStorage);

  const setMember = useCallback((m: Member, apiKey: string) => {
    sessionStorage.setItem('member', JSON.stringify(m));
    setClientApiKey(apiKey);
    setMemberState(m);
  }, []);

  const clearMember = useCallback(() => {
    sessionStorage.removeItem('member');
    setClientApiKey(null);
    setMemberState(null);
  }, []);

  return (
    <MemberContext.Provider value={{ member, setMember, clearMember }}>
      {children}
    </MemberContext.Provider>
  );
}

export function useMember() {
  const ctx = useContext(MemberContext);
  if (!ctx) throw new Error('useMember must be used inside MemberProvider');
  return ctx;
}
